/**
 * @file FilterBar.tsx
 * @description Единственная строка фильтров списка (phase19.23 этап A):
 * поиск → чипсы → селекты справа.
 *
 * Правило раскладки (чек-лист A6): на `lg` всё в одну строку, поиск не шире
 * 480px, селекты прижаты вправо (`ml-auto`); на узком экране блоки переносятся,
 * пилюли переносятся по строкам (`FilterChips`).
 *
 * phase19.31 (зона «Склад», разбор B; дефект приёмки 2026-10-02 — «Скан
 * или номер заказа» на 1024 сжималось до ширины кнопки): у поиска основа
 * 20rem и минимум 280px, а не нулевая основа —
 * при `flex-1` с нулевой основой длинный ряд чипсов сжимал поле до ~20px
 * («Номер доку…» в перемещениях, остатки на 1280). Теперь, если места не
 * хватает, ряд переносится, а поле остаётся читаемым.
 *
 * phase19.31 (SC59, SC45) — два необязательных режима, остальные списки
 * их не замечают:
 *  - `chipsBelow` — пилюли своим рядом под «поиск + селекты» на всех
 *    ширинах (много пилюль: «Остатки», «Перемещения»);
 *  - `chipsCollapse` — на телефоне пилюли уходят в шторку «Фильтры · N»
 *    (общий `FilterSheetButton`), кнопка встаёт в ряд селектов. Единое
 *    решение склада для пилюль, которые не помещаются в строку на 360.
 *
 * ```tsx
 * <FilterBar
 *   search={<SearchInput value={q} onChange={...} placeholder="Поиск" ariaLabel="Поиск" />}
 *   chips={<FilterToggleChips items={toggles} aria-label="Отбор" />}
 *   chipsBelow
 *   chipsCollapse={{ activeCount: 2 }}
 * >
 *   <SelectField value={warehouseId} onChange={setWarehouseId} options={warehouses} ariaLabel="Склад" />
 * </FilterBar>
 * ```
 */

import type { ReactNode } from "react";

import { FilterSheetButton } from "./FilterSheetButton";
import { PHONE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface Props {
  /** Поле поиска/скана. Ширину задаёт FilterBar, а не само поле. */
  search?: ReactNode;
  /** Пилюли-фильтры (`FilterChips` / `FilterToggleChips`). */
  chips?: ReactNode;
  /** Селекты и переключатели — уезжают вправо на `lg`. */
  children?: ReactNode;
  className?: string;
  /** Пилюли своим рядом под поиском и селектами (на всех ширинах). */
  chipsBelow?: boolean;
  /**
   * Телефон: пилюли в шторке «Фильтры · N»; `activeCount` — включённые
   * отборы, `onReset` — кнопка «Сбросить» в шторке (не задано — её нет).
   */
  chipsCollapse?: { activeCount: number; onReset?: () => void; title?: string };
}

export function FilterBar({ search, chips, children, className, chipsBelow = false, chipsCollapse }: Props) {
  const phone = useMediaQuery(PHONE_QUERY);
  const collapsed = Boolean(chips && chipsCollapse && phone);

  const sheet = collapsed ? (
    <FilterSheetButton count={chipsCollapse?.activeCount ?? 0} onReset={chipsCollapse?.onReset} title={chipsCollapse?.title}>
      {chips}
    </FilterSheetButton>
  ) : null;
  // min-w-0: иначе длинный ряд чипсов растягивает строку и ломает ml-auto.
  const chipsBlock = chips && !collapsed ? <div className="min-w-0 lg:shrink">{chips}</div> : null;
  const controls =
    children || sheet ? (
      <div className={cn("flex flex-wrap items-center gap-2 lg:shrink-0", chipsBelow ? "md:ml-auto" : "lg:ml-auto")}>
        {children}
        {sheet}
      </div>
    ) : null;

  if (chipsBelow) {
    return (
      <div className={cn("flex flex-col gap-3", className)}>
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
          {search ? (
            <div data-slot="filter-search" className="w-full min-w-0 md:w-auto md:min-w-[280px] md:flex-[1_1_20rem] lg:max-w-[480px]">
              {search}
            </div>
          ) : null}
          {controls}
        </div>
        {chipsBlock}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center",
        className,
      )}
    >
      {search ? (
        <div data-slot="filter-search" className="w-full min-w-0 lg:min-w-[280px] lg:max-w-[480px] lg:flex-[1_1_20rem]">
          {search}
        </div>
      ) : null}
      {chipsBlock}
      {controls}
    </div>
  );
}
